/** BLOCK and INSERT — name a symbol, or place one that already exists.
 *
 * ⚠⚠ **BOTH DIALOGS ASK FOR A NAME AND THEN FOR A POINT ON THE DRAWING, AND THAT ORDER IS THE
 * WHOLE DESIGN.** AutoCAD's Block Definition dialog has a "Pick point" button that dismisses it,
 * takes a click and comes back; there is nothing to come back FOR here, so the dialog closes and
 * the command line asks for the point. That keeps the base point a real snapped pick — which it
 * has to be, because a block whose origin is not on a corner is a block that never lines up.
 *
 * ⚠ The block list comes from the SERVER, with a usage count. A block used 40 times is the
 * office's standard door and one used 0 times is debris; sorting by use puts the list in the
 * order a drafter reaches for it.
 */
import { useEffect, useState } from "react";

import { loadBlocks, type BlockRow } from "./api";

export default function BlockDialog({ mode, drawingId, count, onName, onClose }: {
  /** `"create"` names a new block from the selection; `"insert"` picks an existing one. */
  mode: "create" | "insert";
  drawingId: number;
  /** How many objects are selected — only meaningful when creating. */
  count: number;
  /** The chosen name. ⚠ The dialog does NOT do the work: it hands the name back and the studio
   *  asks for the base point, because that is a click on the drawing. */
  onName: (name: string, opts: { scale: number; rotation: number }) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<BlockRow[]>([]);
  const [name, setName] = useState("");
  const [scale, setScale] = useState("1");
  const [rot, setRot] = useState("0");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadBlocks(drawingId).then((r) => {
      setRows(r);
      if (r.length && !name) setName(r[0].name);
    }).catch(() => { /* an empty list is a real answer for a drawing with no blocks */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on open
  }, [drawingId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const go = () => {
    const n = name.trim();
    if (!n) return setErr("A block needs a name.");
    // ⚠ Checked HERE as well as on the server, because the server's refusal arrives after the
    // dialog has closed and the drafter has already been asked for a base point.
    if (n.startsWith("*")) return setErr("A name cannot start with * — those are AutoCAD's own.");
    if (mode === "create" && rows.some((r) => r.name.toLowerCase() === n.toLowerCase())) {
      return setErr(`This drawing already has a block called "${n}".`);
    }
    const s = Number(scale), a = Number(rot);
    onName(n, { scale: Number.isFinite(s) && s !== 0 ? s : 1,
                rotation: Number.isFinite(a) ? a : 0 });
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 24, zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 430, maxHeight: "100%", display: "flex", flexDirection: "column",
                    background: "#3c3f41", border: "1px solid #555", borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          {mode === "create"
            ? `Block Definition — ${count} object${count === 1 ? "" : "s"}`
            : "Insert Block"}
        </div>
        <div style={{ padding: 12, display: "grid", gap: 10, font: "11.5px system-ui",
                      minHeight: 0, overflow: "auto" }}>
          {mode === "create" ? (
            <label style={{ display: "grid", gap: 3 }}>
              <span style={{ color: "#9aa0a6" }}>Name</span>
              <input autoFocus value={name} data-testid="block-name"
                     onChange={(e) => { setName(e.target.value); setErr(null); }}
                     onKeyDown={(e) => { if (e.key === "Enter") go(); }} style={inp} />
            </label>
          ) : rows.length ? (
            <label style={{ display: "grid", gap: 3 }}>
              <span style={{ color: "#9aa0a6" }}>Block ({rows.length} in this drawing)</span>
              <select value={name} data-testid="block-pick" size={Math.min(10, rows.length)}
                      onChange={(e) => setName(e.target.value)}
                      style={{ ...inp, padding: 0 }}>
                {rows.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.name} — {r.entities} object{r.entities === 1 ? "" : "s"}
                    {r.used ? `, used ${r.used}×` : ", unused"}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div style={{ color: "#c8a35a", lineHeight: 1.5 }}>
              This drawing has no blocks yet. Select some geometry and use Create Block.
            </div>
          )}

          {mode === "insert" && rows.length ? (
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ display: "grid", gap: 3, flex: 1 }}>
                <span style={{ color: "#9aa0a6" }}>Scale</span>
                <input value={scale} onChange={(e) => setScale(e.target.value)}
                       data-testid="block-scale" style={inp} />
              </label>
              <label style={{ display: "grid", gap: 3, flex: 1 }}>
                <span style={{ color: "#9aa0a6" }}>Rotation</span>
                <input value={rot} onChange={(e) => setRot(e.target.value)}
                       data-testid="block-rot" style={inp} />
              </label>
            </div>
          ) : null}

          {/* ⚠ Says what happens NEXT. The dialog closing and the drawing waiting for a click is
              the part a drafter has to be expecting, and AutoCAD's own dialog says the same
              thing with its "Pick point" button. */}
          <div style={{ color: "#9aa0a6", fontSize: 11, lineHeight: 1.5 }}>
            {mode === "create"
              ? "Then click the base point — the corner the block will line up by."
              : "Then click where it goes."}
          </div>
          {err && <div data-testid="block-error" style={{ color: "#ff8f8f" }}>{err}</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button style={btn} onClick={onClose} data-testid="block-cancel">Cancel</button>
            <button style={{ ...btn, background: "#3c5a82", borderColor: "#4a6d9c" }}
                    disabled={mode === "insert" && !rows.length}
                    data-testid="block-ok" onClick={go}>
              {mode === "create" ? "Pick base point" : "Pick insertion point"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "4px 7px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
